import React from 'react'

interface VideoFrame {
  timestamp: number
  label: string
  analysis?: {
    issues: Array<{
      rule: string
      status: 'pass' | 'warning' | 'fail'
      severity: string
    }>
  }
}

interface VideoTimelineProps {
  frames: VideoFrame[]
  duration?: number
  onFrameClick?: (timestamp: number) => void
}

export default function VideoTimeline({ frames, duration = 30, onFrameClick }: VideoTimelineProps) {
  const getIssueColor = (frame: VideoFrame) => {
    if (!frame.analysis?.issues) return 'bg-gray-300'
    
    const hasFailed = frame.analysis.issues.some(i => i.status === 'fail')
    const hasWarning = frame.analysis.issues.some(i => i.status === 'warning')
    
    if (hasFailed) return 'bg-red-500'
    if (hasWarning) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getIssueCount = (frame: VideoFrame) => {
    if (!frame.analysis?.issues) return 0
    return frame.analysis.issues.filter(i => i.status !== 'pass').length
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>0:00</span>
        <span>Video Timeline Analysis</span>
        <span>{Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}</span>
      </div>
      
      {/* Timeline */}
      <div className="relative h-12 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
        {/* Background grid */}
        <div className="absolute inset-0 flex">
          {Array.from({ length: 10 }, (_, i) => (
            <div 
              key={i}
              className="flex-1 border-r border-gray-200 dark:border-gray-700 last:border-0"
            />
          ))}
        </div>
        
        {/* Frame markers */}
        {frames.map((frame, index) => {
          const position = (frame.timestamp / duration) * 100
          const issueCount = getIssueCount(frame)
          
          return (
            <button
              key={index}
              onClick={() => onFrameClick?.(frame.timestamp)}
              className="absolute top-0 bottom-0 w-1 group hover:w-2 transition-all"
              style={{ left: `${position}%` }}
              title={`${frame.label} - ${issueCount} issues`}
            >
              {/* Marker line */}
              <div className={`h-full ${getIssueColor(frame)} transition-colors`} />
              
              {/* Issue badge */}
              {issueCount > 0 && (
                <div className={`absolute -top-6 left-1/2 transform -translate-x-1/2 
                  ${getIssueColor(frame).replace('bg-', 'text-')} 
                  text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity`}>
                  {issueCount}
                </div>
              )}
              
              {/* Tooltip */}
              <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 
                bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap
                opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {frame.label}
                <br />
                {frame.timestamp}s
              </div>
            </button>
          )
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-[10px] text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded-sm" />
          <span>Pass</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-yellow-500 rounded-sm" />
          <span>Warning</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded-sm" />
          <span>Failed</span>
        </div>
      </div>
      
      {/* Frame list */}
      <div className="space-y-1 mt-3">
        {frames.map((frame, index) => {
          const issueCount = getIssueCount(frame)
          return (
            <button
              key={index}
              onClick={() => onFrameClick?.(frame.timestamp)}
              className="w-full flex items-center justify-between p-2 rounded 
                hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getIssueColor(frame)}`} />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {frame.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500">{frame.timestamp}s</span>
                {issueCount > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    getIssueColor(frame).replace('bg-', 'bg-').replace('500', '100')
                  } ${getIssueColor(frame).replace('bg-', 'text-')}`}>
                    {issueCount} issue{issueCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}


